import type { JobStatus } from '@conversia/shared-types';

/**
 * Agrupación y orden de trabajos para /jobs y /mis-trabajos.
 *
 * La lista plana no escala: el operador piensa en "qué falta, qué está pasando,
 * qué ya terminó". Se agrupa por esa semántica y dentro de cada grupo se ordena
 * por hora de cita (la agenda real del día), no por fecha de creación.
 */

export type JobGroupKey = 'pendientes' | 'en_curso' | 'por_validar' | 'terminados';

export const JOB_GROUP_LABELS: Record<JobGroupKey, string> = {
  pendientes: 'Pendientes',
  en_curso: 'En curso',
  por_validar: 'Por validar',
  terminados: 'Terminados',
};

const GROUP_ORDER: JobGroupKey[] = ['pendientes', 'en_curso', 'por_validar', 'terminados'];

export function jobGroupKey(estado: JobStatus): JobGroupKey {
  if (estado === 'ASIGNADO') return 'pendientes';
  if (estado === 'EN_SITIO') return 'en_curso';
  // S-01: el técnico terminó pero el trabajo NO está cerrado. Va en su propio
  // grupo: mezclarlo con los terminados esconde el trabajo que le queda al panel.
  if (estado === 'PENDIENTE_VALIDACION') return 'por_validar';
  return 'terminados';
}

interface GroupableJob {
  estado: JobStatus;
  scheduledAt?: string | null;
}

/** Milisegundos de la cita, o +Infinity para "sin cita" (van al final del grupo). */
function citaMs(job: GroupableJob): number {
  if (!job.scheduledAt) return Number.POSITIVE_INFINITY;
  const t = new Date(job.scheduledAt).getTime();
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

export interface JobGroup<T extends GroupableJob> {
  key: JobGroupKey;
  label: string;
  jobs: T[];
}

/**
 * Parte la lista en Pendientes / En curso / Por validar / Terminados (en ese
 * orden) y ordena
 * cada grupo por hora de cita ascendente; los trabajos sin cita cierran el grupo.
 * Los grupos vacíos no se devuelven (la UI no pinta encabezados huecos).
 */
export function groupJobs<T extends GroupableJob>(jobs: T[]): JobGroup<T>[] {
  const buckets: Record<JobGroupKey, T[]> = {
    pendientes: [],
    en_curso: [],
    por_validar: [],
    terminados: [],
  };
  for (const job of jobs) buckets[jobGroupKey(job.estado)].push(job);
  return GROUP_ORDER.filter((key) => buckets[key].length > 0).map((key) => ({
    key,
    label: JOB_GROUP_LABELS[key],
    jobs: [...buckets[key]].sort((a, b) => citaMs(a) - citaMs(b)),
  }));
}

/** "2 h 05 min" / "35 min" a partir de una diferencia absoluta en ms. */
function duracionCorta(ms: number): string {
  const totalMin = Math.max(1, Math.round(ms / 60_000));
  if (totalMin < 60) return `${totalMin} min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, '0')} min`;
}

export interface EsperaInfo {
  /** true = la cita ya pasó y el trabajo sigue pendiente. */
  vencida: boolean;
  /** "Faltan 25 min" / "Vencida hace 1 h 10 min". */
  label: string;
}

/**
 * Tiempo de espera de un trabajo PENDIENTE respecto a su cita: cuánto falta o
 * cuánto lleva vencido. Devuelve null si no hay cita (no hay contra qué medir).
 */
export function esperaPendiente(
  scheduledAt: string | null | undefined,
  now: Date = new Date(),
): EsperaInfo | null {
  if (!scheduledAt) return null;
  const cita = new Date(scheduledAt).getTime();
  if (Number.isNaN(cita)) return null;
  const diff = cita - now.getTime();
  if (diff >= 0) return { vencida: false, label: `Faltan ${duracionCorta(diff)}` };
  return { vencida: true, label: `Vencida hace ${duracionCorta(-diff)}` };
}
